/* Motion Kit — das Bewegungsgefühl der Markething-Seite, portabel.
   Keine Abhängigkeiten. In jedes Projekt kopierbar (ES-Modul + window.MotionKit).

   Drei Bausteine tragen die "Smoothheit":
   1. EINE Kurve für alles (EASE_CSS / ease)  — Bewegung wirkt aus einer Hand.
   2. Reveals per IntersectionObserver, nie am Scrollwert hängend — kein Ruckeln,
      kein Layout-Thrash, kein Scroll-Handler pro Element.
   3. Zeigerwerte laufen über lerp im requestAnimationFrame, nie direkt aus dem
      Event — das ist der eigentliche Grund, warum sich Hover "gefedert" anfühlt.
*/

export const EASE_CSS = 'cubic-bezier(.11,.82,.39,.92)';

/* Dauern-Skala: kurz für Farbe, mittel für Verschiebung, lang für Fläche.
   Immer aus dieser Leiter greifen, keine Zwischenwerte erfinden. */
export const DUR = { color: 400, tick: 520, move: 560, text: 640, area: 700, page: 1100 };

/* Staffelung: der letzte führt, der Rest kaskadiert zurück. */
export const STAGGER = 60; // ms

export const reduced = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Dieselbe Kurve als JS-Funktion (für canvas/WebGL/manuelle Tweens). */
export function bez(x1, y1, x2, y2) {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const fx = t => ((ax * t + bx) * t + cx) * t;
  const dfx = t => (3 * ax * t + 2 * bx) * t + cx;
  return p => {
    let t = p;
    for (let i = 0; i < 6; i++) {
      const e = fx(t) - p, d = dfx(t);
      if (Math.abs(e) < 1e-5 || Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    t = Math.max(0, Math.min(1, t));
    return ((ay * t + by) * t + cy) * t;
  };
}
export const ease = bez(.11, .82, .39, .92);

export const transition = (props, ms = DUR.move, delay = 0) =>
  props.split(',').map(p => p.trim() + ' ' + ms + 'ms ' + EASE_CSS +
    (delay ? ' ' + delay + 'ms' : '')).join(', ');

/* Transitionen ergänzen statt überschreiben: zwei Helfer dürfen dieselbe
   Fläche anfassen, ohne sich die Eigenschaften des anderen wegzuschreiben.
   Verzögerungen stehen immer in der Kurzschrift, nie in transitionDelay —
   sonst zerschießt eine zweite Zuweisung die Staffelung. */
export function addTransition(el, entries) {
  const list = String(el.style.transition || '').split(',')
    .map(s => s.trim()).filter(Boolean);
  const owned = list.map(s => s.split(/\s+/)[0]);
  entries.forEach(e => {
    if (owned.indexOf(e.split(/\s+/)[0]) === -1) list.push(e);
  });
  el.style.transition = list.join(', ');
}

/* ---------------------------------------------------------------- Reveal --
   reveal('[data-reveal]') blendet Elemente beim Eintritt ein und staffelt
   Geschwister-Gruppen. Optionen pro Element über data-Attribute:
     data-reveal="up|down|left|right|fade"   Richtung  (Standard: up)
     data-reveal-delay="120"                 zusätzliche Verzögerung in ms
     data-reveal-group                       staffelt direkte Kinder
   Sicherheitsnetz: was nach `failsafe` ms sichtbar ist, wird gesetzt — nie
   bleibt Inhalt unsichtbar, wenn ein Observer nicht feuert.
*/
export function reveal(selector = '[data-reveal]', opts = {}) {
  const {
    root = document, distance = 24, threshold = 0.15,
    margin = '0px 0px -10% 0px', duration = DUR.page, failsafe = 2500
  } = opts;

  const nodes = Array.prototype.slice.call(root.querySelectorAll(selector));
  const off = { up: `translateY(${distance}px)`, down: `translateY(-${distance}px)`,
                left: `translateX(${distance}px)`, right: `translateX(-${distance}px)`, fade: 'none' };

  const kidsOf = el => el.hasAttribute('data-reveal-group')
    ? Array.prototype.slice.call(el.children) : [];

  const show = el => {
    el.style.opacity = '1';
    el.style.transform = 'none';
    kidsOf(el).forEach(k => { k.style.opacity = '1'; k.style.transform = 'none'; });
  };

  if (reduced()) { nodes.forEach(show); return () => {}; }

  const hide = (el, dir, delay) => {
    el.style.opacity = '0';
    el.style.transform = off[dir] || off.up;
    el.style.transition = transition('opacity, transform', duration, delay);
    el.style.willChange = 'opacity, transform';
  };

  // Nach dem Auftritt gibt reveal() die Fläche frei: transition zurücksetzen,
  // damit Hover-Helfer wie lift() ihre eigenen Dauern bekommen.
  const release = el => {
    el.style.transition = '';
    el.style.willChange = 'auto';
  };

  // Was beim Start schon im Bild ist, ist bereits gemalt: nicht verstecken und
  // neu einblenden — sonst zählt Google das zweite Erscheinen als LCP
  // (SEO-Audit 2026-10-08, bis 1,8 s Render-Verzug). Erst alle messen, dann schreiben.
  const vh = window.innerHeight;
  const inView = nodes.map(el => {
    const r = el.getBoundingClientRect();
    return r.top < vh && r.bottom > 0;
  });
  const later = nodes.filter((el, i) => !inView[i]);

  later.forEach(el => {
    const dir = el.dataset.reveal;
    const kids = kidsOf(el);
    if (kids.length) {
      // Die Kinder tragen die Bewegung, der Rahmen bleibt stehen — sonst
      // laufen zwei Transitionen übereinander und die Staffelung verpufft.
      el.style.opacity = '1';
      el.style.transform = 'none';
      kids.forEach((k, i) => hide(k, dir, i * STAGGER));
    } else {
      hide(el, dir);
    }
  });

  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      const el = e.target;
      io.unobserve(el);
      const base = parseInt(el.dataset.revealDelay || '0', 10);
      setTimeout(() => {
        show(el);
        const kids = kidsOf(el);
        const last = kids.slice(-1)[0] || el;
        last.addEventListener('transitionend', () => {
          release(el);
          kids.forEach(release);
        }, { once: true });
      }, base);
    });
  }, { threshold, rootMargin: margin });

  later.forEach(el => io.observe(el));

  const net = setTimeout(() => {
    nodes.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) show(el);
    });
  }, failsafe);

  return () => { io.disconnect(); clearTimeout(net); };
}

/* ------------------------------------------------------------- Smoothing --
   smooth(fn, factor) liefert einen Setter: Zielwerte kommen aus Events,
   ausgegeben wird der gefederte Wert, einmal pro Frame. factor 0.055 ist die
   Trägheit der Markething-Shader; 0.12 fühlt sich straffer an.
*/
export function smooth(apply, factor = 0.055, start = [0, 0]) {
  const cur = start.slice(), target = start.slice();
  let raf = 0, live = true;
  const frame = () => {
    if (!live) return;
    raf = requestAnimationFrame(frame);
    let moved = false;
    for (let i = 0; i < target.length; i++) {
      const d = target[i] - cur[i];
      if (Math.abs(d) > 1e-4) { cur[i] += d * factor; moved = true; }
      else cur[i] = target[i];
    }
    if (moved) apply(cur);
  };
  raf = requestAnimationFrame(frame);
  return {
    set: (...v) => { v.forEach((n, i) => { target[i] = n; }); },
    stop: () => { live = false; cancelAnimationFrame(raf); }
  };
}

/* Zeiger-Parallaxe: gefederter, normalisierter Zeiger (-1…1) für ein Element. */
export function pointerSmooth(el, apply, factor = 0.055) {
  const s = smooth(apply, factor);
  const onMove = e => {
    const r = el.getBoundingClientRect();
    s.set((e.clientX - r.left) / r.width * 2 - 1, (e.clientY - r.top) / r.height * 2 - 1);
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerleave', () => s.set(0, 0));
  return () => { el.removeEventListener('pointermove', onMove); s.stop(); };
}

/* ----------------------------------------------------------------- Hover --
   lift(el) — die Projektkarten-Bewegung: anheben, minimal skalieren, Rahmen
   mitfärben. Alles über transform, nie über top/height.
*/
export function lift(el, o = {}) {
  const { y = -10, scale = 1.015, border = null, ms = DUR.area } = o;
  const base = el.style.borderColor;
  // Erst beim ersten Hover verkabeln: bis dahin kann reveal() dieselbe Fläche
  // noch einblenden, ohne dass sich die beiden Transitionen überschreiben.
  let wired = false;
  el.addEventListener('pointerenter', () => {
    if (!wired) {
      addTransition(el, [transition('transform', ms), 'border-color ' + DUR.color + 'ms ' + EASE_CSS]);
      wired = true;
    }
    el.style.transform = `translateY(${y}px) scale(${scale})`;
    if (border) el.style.borderColor = border;
  });
  el.addEventListener('pointerleave', () => {
    el.style.transform = 'none';
    if (border) el.style.borderColor = base;
  });
}

/* ---------------------------------------------------------------- Scroll --
   Kein Scroll-Hijacking (Trägheits-Scroller kämpfen mit Trackpads und
   brechen Ankersprünge). Stattdessen: native Glätte + korrekte Anker.
   In <style>:  html { scroll-behavior: smooth; scroll-padding-top: <Headerhöhe> }
   Dazu content-visibility für lange Seiten, damit Frames nicht einbrechen.
*/
export function scrollSetup({ headerHeight = 0 } = {}) {
  const root = document.documentElement;
  root.style.scrollBehavior = reduced() ? 'auto' : 'smooth';
  if (headerHeight) root.style.scrollPaddingTop = headerHeight + 'px';
}

/* Ein Aufruf für den Normalfall. */
export function init(opts = {}) {
  scrollSetup(opts);
  return reveal(opts.selector || '[data-reveal]', opts);
}

const MotionKit = { EASE_CSS, DUR, STAGGER, ease, bez, transition, addTransition,
                    reveal, smooth, pointerSmooth, lift, scrollSetup, init, reduced };
if (typeof window !== 'undefined') window.MotionKit = MotionKit;
export default MotionKit;
